import React, { useEffect } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import Dashboard from "./scenes/dashboard";
import Sidebar from "./scenes/global/Sidebar";
import AddTest from "./scenes/add";
import { Auth0Provider } from "@auth0/auth0-react";
import VideoProvider from "./context/video_context";
import { useLocation } from "react-router-dom";
import APIProvider from "./context/api_hook";
import useAuth from "./hooks/auth_hook";
import usePayment from "./hooks/payment_hook";
import { Toaster } from "./components/ui/sonner";
import { Spinner } from "./components/ui/spinner";
import Tests from "./scenes/tests";
import Test from "./scenes/test";
import TestProvider from "./context/test_context";
import Settings from "./scenes/settings";
import ViewProvider from "./context/view_context";
import CurrentTests from "./scenes/current";
import FinishedTests from "./scenes/finished";
import MetricsProvider from "./context/metrics_context";
import Checkout from "./scenes/checkout";

interface RouteConfig {
  path: string;
  element: React.ReactNode;
  index?: boolean;
}

const IS_DEBUG = process.env.REACT_APP_DEBUG === "true";

function LogOut() {
  const { logout } = useAuth();
  console.log("LOGGING OUT");
  logout();
  return <div></div>;
}

const Home = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPaid, isCheckingPayment } = usePayment();

  useEffect(() => {
    if (isCheckingPayment) return;
    if (location.pathname === "/") {
      if (hasPaid) {
        navigate("/add");
      } else {
        navigate("/checkout");
      }
    }
  }, [location, navigate, hasPaid, isCheckingPayment]);

  return (
    <div className="flex w-full justify-center items-center h-screen bg-background">
      <Spinner size="lg" />
    </div>
  );
};

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();

  const public_routes: RouteConfig[] = [
    { path: "/", element: <Home /> },
    { path: "/checkout", element: <Checkout /> },
    { path: "/logout", element: <LogOut /> },
  ];

  const paid_routes: RouteConfig[] = [
    { path: "/add", element: <AddTest />, index: true },
    { path: "/settings", element: <Settings /> },
    { path: "/tests", element: <Tests /> },
    { path: "/current", element: <CurrentTests /> },
    { path: "/finished", element: <FinishedTests /> },
    { path: "/tests/:testid", element: <Test /> },
    { path: "/previous", element: <Dashboard /> },
    { path: "/metrics", element: <Dashboard /> },
  ];

  const authenticated_routes: RouteConfig[] = [...paid_routes, ...public_routes];

  const { loginWithRedirect, isAuthenticated, isLoading, user } = useAuth();
  const { hasPaid, isCheckingPayment } = usePayment();

  const is_authenticated_route = authenticated_routes.some(
    (route) => route.path === location.pathname
  );

  if (is_authenticated_route && !isAuthenticated && !isLoading && !IS_DEBUG) {
    loginWithRedirect();
  }

  const is_paid_route = paid_routes.some((route) => route.path === location.pathname);

  useEffect(() => {
    if (!isLoading && !isCheckingPayment && is_paid_route && !hasPaid) {
      navigate("/checkout");
    }
  }, [isLoading, isCheckingPayment, hasPaid, is_paid_route, navigate]);

  return (
    <div className="flex h-screen w-full bg-background text-foreground overflow-hidden">
      {!isLoading && (user || IS_DEBUG) && !(is_paid_route && isCheckingPayment) ? (
        <TestProvider>
          <VideoProvider>
            <MetricsProvider>
              <ViewProvider>
                <Sidebar user={user} />
                <main className="flex-1 overflow-y-auto">
                  <Routes>
                    {authenticated_routes.map((route) => (
                      <Route
                        key={route.path}
                        path={route.path}
                        element={route.element}
                        index={route.index}
                      />
                    ))}
                  </Routes>
                </main>
              </ViewProvider>
            </MetricsProvider>
          </VideoProvider>
        </TestProvider>
      ) : (
        <div className="flex w-full justify-center items-center h-screen bg-background">
          <Spinner size="lg" />
        </div>
      )}
    </div>
  );
}

function App() {
  return (
    <Auth0Provider
      domain="auth.forcreators.xyz"
      clientId="ODlnTxjgjCY92KsE045tUeS4klb9k2MZ"
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: "https://dev-6paf5mmzujgbxltb.us.auth0.com/api/v2/",
        scope: "read:current_user update:current_user_metadata",
      }}
      cacheLocation="localstorage"
      useRefreshTokens={true}
    >
      <APIProvider>
        <AppContent />
        <Toaster position="bottom-right" richColors />
      </APIProvider>
    </Auth0Provider>
  );
}

export default App;
